// src/components/portal/verifyauthentication.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import "./portal-components.css";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../config/apiConfig";

import IconAdd from "./res/Icon-Add.svg";
import IconEdit from "./res/Icon-Edit.svg";
import IconTrash from "./res/Icon-Trash.svg";
import IconKey from "./res/Icon-Key.svg";
import IconTest from "./res/Icon-Test.svg";

const STATUS_POLL_INTERVAL = 5000;

const VerifyAuthentication = () => {
  const { user } = useAuth();
  
  const [profiles, setProfiles] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [showTestModal, setShowTestModal] = useState(null);
  const [testingCredentials, setTestingCredentials] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [selectedTestDevice, setSelectedTestDevice] = useState("");
  
  const [formData, setFormData] = useState({
    profile_name: "",
    cred_username: "",
    cred_password: "",
    enable_password: "",
    description: ""
  });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const pollRef = useRef(null);

  // Fetch profiles
  const fetchProfiles = useCallback(async (showLoading = true) => {
    if (!user?.username) return;
    try {
      if (showLoading) setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/auth/profiles?username=${encodeURIComponent(user.username)}`);
      const data = await res.json();
      if (data.status === "success") {
        setProfiles(data.data?.profiles || []);
      }
    } catch (err) {
      console.error("Failed to fetch profiles:", err);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [user?.username]);

  // Fetch devices for testing
  const fetchDevices = useCallback(async () => {
    if (!user?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/services/devices?username=${encodeURIComponent(user.username)}`);
      const data = await res.json();
      if (data.status === "success") {
        setDevices(data.data?.devices || []);
      }
    } catch (err) {
      console.error("Failed to fetch devices:", err);
    }
  }, [user?.username]);

  useEffect(() => {
    fetchProfiles();
    fetchDevices();
  }, [fetchProfiles, fetchDevices]);

  // Poll for status updates
  useEffect(() => {
    pollRef.current = setInterval(() => {
      fetchProfiles(false);
    }, STATUS_POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchProfiles]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setFormError("");
    
    if (!formData.profile_name.trim()) {
      setFormError("Profile name is required");
      return;
    }
    if (!formData.cred_username.trim()) {
      setFormError("Username is required");
      return;
    }
    if (!editingProfile && !formData.cred_password) {
      setFormError("Password is required");
      return;
    }
    
    setSaving(true);
    try {
      const endpoint = editingProfile 
        ? `${API_BASE_URL}/api/auth/profiles/${editingProfile.id}`
        : `${API_BASE_URL}/api/auth/profiles`;
      
      const res = await fetch(endpoint, {
        method: editingProfile ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user.username, ...formData })
      });
      
      const data = await res.json();
      if (data.status === "success") {
        setShowForm(false);
        setEditingProfile(null);
        setFormData({ profile_name: "", cred_username: "", cred_password: "", enable_password: "", description: "" });
        fetchProfiles();
      } else {
        setFormError(data.reason || "Failed to save profile");
      }
    } catch (err) {
      setFormError("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleEditProfile = (profile) => {
    setEditingProfile(profile);
    setFormData({
      profile_name: profile.profile_name,
      cred_username: profile.username,
      cred_password: "", // Don't populate password
      enable_password: "",
      description: profile.description || ""
    });
    setShowForm(true);
    setFormError("");
  };

  const handleDeleteProfile = async (profile) => {
    if (!window.confirm(`Delete profile "${profile.profile_name}"?`)) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/profiles/${profile.id}?username=${encodeURIComponent(user.username)}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchProfiles();
      } else {
        alert(data.detail || data.reason || "Failed to delete profile");
      }
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleOpenTestModal = (profile) => {
    setShowTestModal(profile);
    setSelectedTestDevice("");
    setTestResult(null);
  };

  const handleTestCredentials = async () => {
    if (!selectedTestDevice) {
      setTestResult({ success: false, message: "Please select a device" });
      return;
    }
    
    setTestingCredentials(true);
    setTestResult(null);
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/profiles/${showTestModal.id}/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          username: user.username,
          device_id: selectedTestDevice 
        })
      });
      
      const data = await res.json();
      if (data.status === "success") {
        setTestResult({ success: true, message: "Connection successful!" });
        fetchProfiles(); // Refresh to update status
      } else {
        setTestResult({ success: false, message: data.reason || "Connection failed" });
      }
    } catch (err) {
      setTestResult({ success: false, message: "Network error during test" });
    } finally {
      setTestingCredentials(false);
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'success': return { class: 'status-success', text: 'Verified' };
      case 'failed': return { class: 'status-failed', text: 'Failed' };
      default: return { class: 'status-untested', text: 'Not Tested' };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleString();
  };

  return (
    <>
      <div className="right-top">
        <div className="right-top-title">Authentication Profiles</div>
      </div>

      <div className="right-middle auth-middle">
        {/* CREDENTIALS PROFILES SECTION */}
        <div className="service-section">
          <div className="cell service-cell">
            <div className="cell-header">
              <span>Credentials Profiles</span>
              <div className="header-actions">
                <button className="icon-btn add-btn" onClick={() => { 
                  setShowForm(true); 
                  setEditingProfile(null); 
                  setFormData({ profile_name: "", cred_username: "", cred_password: "", enable_password: "", description: "" }); 
                  setFormError(""); 
                }}>
                  <img src={IconAdd} alt="Add" />
                  <span>Add Profile</span>
                </button>
              </div>
            </div>
            <div className="cell-body">
              {loading ? (
                <div className="loading">Loading profiles...</div>
              ) : profiles.length === 0 ? (
                <div className="empty-state">
                  <img src={IconKey} alt="No profiles" className="empty-icon" />
                  <p>No authentication profiles created</p>
                  <p className="empty-hint">Create profiles to store credentials for network device access</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Profile Name</th>
                        <th>Username</th>
                        <th>Status</th>
                        <th>Last Tested</th>
                        <th>Tested On</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profiles.map(profile => {
                        const statusInfo = getStatusInfo(profile.last_test_status);
                        return (
                          <tr key={profile.id}>
                            <td>
                              <span className="hostname">{profile.profile_name}</span>
                              {profile.description && <span className="description">{profile.description}</span>}
                            </td>
                            <td><code>{profile.username}</code></td>
                            <td>
                              <span className={`status-badge ${statusInfo.class}`}>
                                <span className="status-dot"></span>
                                {statusInfo.text}
                              </span>
                            </td>
                            <td className="date-cell">{formatDate(profile.last_test_at)}</td>
                            <td className="date-cell">{profile.last_test_device_name || "-"}</td>
                            <td className="actions-cell">
                              <button className="icon-btn-sm" onClick={() => handleOpenTestModal(profile)} title="Test Credentials">
                                <img src={IconTest} alt="Test" />
                              </button>
                              <button className="icon-btn-sm edit" onClick={() => handleEditProfile(profile)} title="Edit">
                                <img src={IconEdit} alt="Edit" />
                              </button>
                              <button className="icon-btn-sm delete" onClick={() => handleDeleteProfile(profile)} title="Delete">
                                <img src={IconTrash} alt="Delete" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CREDENTIALS TEST SECTION */}
        <div className="service-section">
          <div className="cell service-cell">
            <div className="cell-header">
              <span>Test Credentials</span>
            </div>
            <div className="cell-body">
              {profiles.length === 0 ? (
                <div className="empty-state">
                  <p>Create a profile first to test credentials</p>
                </div>
              ) : devices.length === 0 ? (
                <div className="empty-state">
                  <p>No devices available</p>
                  <p className="empty-hint">Add network devices in Service Connections first</p>
                </div>
              ) : (
                <div className="quick-test-panel">
                  <p className="panel-hint">Select a profile and device to test credentials</p>
                  <div className="form-row">
                    <label>Profile</label>
                    <select id="quickTestProfile">
                      <option value="">Select profile...</option>
                      {profiles.map(p => (
                        <option key={p.id} value={p.id}>{p.profile_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-row">
                    <label>Device</label>
                    <select id="quickTestDevice">
                      <option value="">Select device...</option>
                      {devices.map(d => (
                        <option key={d.id} value={d.id}>{d.hostname} ({d.ip_address})</option>
                      ))}
                    </select>
                  </div>
                  <button className="btn-primary" style={{ width: '100%', marginTop: '8px' }} onClick={() => {
                    const profileId = document.getElementById('quickTestProfile').value;
                    const deviceId = document.getElementById('quickTestDevice').value;
                    if (profileId && deviceId) {
                      const profile = profiles.find(p => p.id === parseInt(profileId));
                      if (profile) {
                        setShowTestModal(profile);
                        setSelectedTestDevice(deviceId);
                        setTestResult(null);
                      }
                    }
                  }}>
                    Test Connection
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PROFILE FORM MODAL */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProfile ? "Edit Profile" : "Create Profile"}</h3>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSaveProfile}>
              <div className="form-row">
                <label>Profile Name *</label>
                <input 
                  type="text" 
                  value={formData.profile_name} 
                  onChange={e => setFormData({...formData, profile_name: e.target.value})} 
                  placeholder="e.g., Admin Credentials" 
                />
              </div>
              <div className="form-row">
                <label>Username *</label>
                <input 
                  type="text" 
                  value={formData.cred_username} 
                  onChange={e => setFormData({...formData, cred_username: e.target.value})} 
                  placeholder="SSH username" 
                />
              </div>
              <div className="form-row">
                <label>Password {editingProfile ? "(leave blank to keep)" : "*"}</label>
                <input 
                  type="password" 
                  value={formData.cred_password} 
                  onChange={e => setFormData({...formData, cred_password: e.target.value})} 
                  placeholder="SSH password" 
                />
              </div>
              <div className="form-row">
                <label>Enable Password (Optional)</label>
                <input 
                  type="password" 
                  value={formData.enable_password} 
                  onChange={e => setFormData({...formData, enable_password: e.target.value})} 
                  placeholder="Privilege/enable password" 
                />
              </div>
              <div className="form-row">
                <label>Description (Optional)</label>
                <textarea 
                  value={formData.description} 
                  onChange={e => setFormData({...formData, description: e.target.value})} 
                  placeholder="Enter description..." 
                  rows="2" 
                />
              </div>
              {formError && <div className="form-error">{formError}</div>}
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TEST CREDENTIALS MODAL */}
      {showTestModal && (
        <div className="modal-overlay" onClick={() => setShowTestModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Test Credentials: {showTestModal.profile_name}</h3>
              <button className="close-btn" onClick={() => setShowTestModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: '#6b7280' }}>
                Select a device to test SSH connection with this profile's credentials.
              </p>
              
              {devices.length === 0 ? (
                <div className="empty-state">
                  <p>No devices available</p>
                  <p className="empty-hint">Add network devices in Service Connections first</p>
                </div>
              ) : (
                <>
                  <div className="form-row">
                    <label>Select Device</label>
                    <select 
                      value={selectedTestDevice} 
                      onChange={e => setSelectedTestDevice(e.target.value)}
                    >
                      <option value="">Choose a device...</option>
                      {devices.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.hostname} ({d.ip_address})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {testResult && (
                    <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                      {testResult.success ? '✓' : '✗'} {testResult.message}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setShowTestModal(null)}>Close</button>
              {devices.length > 0 && (
                <button 
                  className="btn-primary" 
                  onClick={handleTestCredentials}
                  disabled={testingCredentials || !selectedTestDevice}
                >
                  {testingCredentials ? "Testing..." : "Test Connection"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="right-bottom-bar"></div>
    </>
  );
};

export default VerifyAuthentication;
